import { useCallback } from 'react';
import { cartService } from '../services/cart.service';
import { useCartStore } from '../store/cartStore';
import { analyticsService } from '../services/analytics.service';
import { sanitizeImageUrl } from '../utils/productImages';
import type { ProductDetail, ProductListItem } from '../types';

/**
 * Drives the UI from the local Zustand cart (instant, reliable) while
 * best-effort mirroring writes to the server cart for authenticated users.
 * Server sync failures never block or roll back the optimistic UI update.
 */
export function useCart() {
  const addLine = useCartStore((s) => s.addLine);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const removeLine = useCartStore((s) => s.removeLine);

  const addToCart = useCallback(
    (product: ProductListItem | ProductDetail, quantity = 1, variantId?: string) => {
      const pId = String((product as any).id || (product as any)._id || '');
      const image = sanitizeImageUrl(product.images?.[0]?.url, product.slug || product.name, 0);
      addLine({
        productId: pId,
        variantId,
        name: product.name,
        slug: product.slug,
        image,
        price: product.price,
        quantity,
      });
      analyticsService.track('add_to_cart', { metadata: { productId: pId, quantity } }).catch(() => {});
      if (pId && /^[0-9a-fA-F]{24}$/.test(pId)) {
        cartService.addItem({ productId: pId, variantId, quantity }).catch(() => {});
      }
    },
    [addLine],
  );

  const changeQuantity = useCallback(
    (lineId: string, quantity: number) => {
      updateQuantity(lineId, quantity);
      cartService.updateItem(lineId, quantity).catch(() => {});
    },
    [updateQuantity],
  );

  const removeFromCart = useCallback(
    (lineId: string) => {
      removeLine(lineId);
      analyticsService.track('remove_from_cart', { metadata: { lineId } }).catch(() => {});
      cartService.removeItem(lineId).catch(() => {});
    },
    [removeLine],
  );

  return { addToCart, changeQuantity, removeFromCart };
}
